import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { getViewConfigByNameAdmin } from '../services/viewConfigService';
import '../styles/adminPromotionalPopup.css';

const CONFIG_NAME = 'PROMOTIONAL_POPUP';

const DEFAULT_CONFIG = {
  enabled: true,
  delay: 500,
  showOnMobile: true,
  showOnDesktop: true,
  button: {
    enabled: false,
    text: '',
    link: '',
  },
  image: {
    url: '',
    publicId: '',
    alt: '',
  }
};

const AdminPromotionalPopup = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving]     = useState(false);
  const [message, setMessage]       = useState(null);

  // Global settings
  const [enabled, setEnabled]             = useState(true);
  const [delay, setDelay]                 = useState(500);
  const [showOnMobile, setShowOnMobile]   = useState(true);
  const [showOnDesktop, setShowOnDesktop] = useState(true);

  // Button settings
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [buttonText, setButtonText]       = useState('');
  const [buttonLink, setButtonLink]       = useState('');

  // Image settings
  const [imageMetadata, setImageMetadata]   = useState({ url: '', publicId: '', alt: '' });
  const [imageFile, setImageFile]           = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  // Auth guard
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Load config on mount
  useEffect(() => {
    const load = async () => {
      try {
        setIsFetching(true);
        const doc = await getViewConfigByNameAdmin(CONFIG_NAME);
        applyDoc(doc);
      } catch (err) {
        // 404 means no document yet — use defaults silently
        if (!err.message?.includes('404')) {
          showMessage('error', 'Failed to load configuration');
        }
      } finally {
        setIsFetching(false);
      }
    };
    load();
  }, []);

  const applyDoc = (doc) => {
    const d = doc?.data || {};
    setEnabled(d.enabled ?? DEFAULT_CONFIG.enabled);
    setDelay(d.delay ?? DEFAULT_CONFIG.delay);
    setShowOnMobile(d.showOnMobile ?? DEFAULT_CONFIG.showOnMobile);
    setShowOnDesktop(d.showOnDesktop ?? DEFAULT_CONFIG.showOnDesktop);
    
    const btn = d.button || {};
    setButtonEnabled(btn.enabled ?? DEFAULT_CONFIG.button.enabled);
    setButtonText(btn.text ?? DEFAULT_CONFIG.button.text);
    setButtonLink(btn.link ?? DEFAULT_CONFIG.button.link);

    const img = d.image || {};
    const meta = {
      url: img.url ?? DEFAULT_CONFIG.image.url,
      publicId: img.publicId ?? DEFAULT_CONFIG.image.publicId,
      alt: img.alt ?? DEFAULT_CONFIG.image.alt,
    };
    setImageMetadata(meta);
    setImagePreviewUrl(meta.url);
    setImageFile(null);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'Image size must be less than 5MB');
      return;
    }

    // Check type
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      showMessage('error', 'Only JPG, JPEG, PNG, and WEBP image files are allowed');
      return;
    }

    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreviewUrl('');
    setImageMetadata({ url: '', publicId: '', alt: '' });
  };

  const validate = () => {
    const d = Number(delay);
    if (isNaN(d) || d < 0 || d > 10000) {
      showMessage('error', 'Delay must be a non-negative number between 0 and 10000 ms');
      return false;
    }

    if (buttonEnabled) {
      if (!buttonText.trim()) {
        showMessage('error', 'Button Text is required when CTA Button is enabled');
        return false;
      }
      if (!buttonLink.trim()) {
        showMessage('error', 'Button Link is required when CTA Button is enabled');
        return false;
      }
    }

    const link = buttonLink.trim();
    if (link !== '') {
      const isValidLink = /^\/|^https?:\/\//.test(link);
      if (!isValidLink) {
        showMessage('error', 'Button Link is invalid. Must start with "/" (internal route) or be a valid HTTP/HTTPS URL');
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);

    const formData = new FormData();
    formData.append('enabled', enabled);
    formData.append('delay', Number(delay));
    formData.append('showOnMobile', showOnMobile);
    formData.append('showOnDesktop', showOnDesktop);
    
    formData.append('button', JSON.stringify({
      enabled: buttonEnabled,
      text: buttonText.trim(),
      link: buttonLink.trim(),
    }));

    if (imageFile) {
      formData.append('image', imageFile);
      formData.append('imageAlt', 'Promotional Popup Image');
    } else {
      formData.append('image', JSON.stringify(imageMetadata));
    }

    try {
      const res = await apiFetch(`/api/view-configs/admin/${CONFIG_NAME}`, {
        method: 'PUT',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Failed to save configuration');
      }

      applyDoc(data);
      showMessage('success', 'Configuration saved successfully');

      // Dispatch event to refresh useViewConfig hooks instantly
      window.dispatchEvent(
        new CustomEvent('viewconfig:invalidate', { detail: { name: CONFIG_NAME } })
      );
    } catch (err) {
      showMessage('error', err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pp-admin-container">
      
      {/* Header */}
      <div className="pp-admin-header">
        <div>
          <h2 className="pp-admin-title">Promotional Popup</h2>
          <p className="pp-admin-subtitle">
            Configure the dynamic promotional popup modal shown to users once per session.
          </p>
        </div>
        <button
          className="btn"
          onClick={() => navigate('/admin')}
          style={{ height: '45px', background: 'var(--btn-secondary-bg, #3f3f46)', color: 'var(--color-text-primary)' }}
        >
          Back to Dashboard
        </button>
      </div>

      {/* Toast Message */}
      {message && (
        <div className={`pp-admin-message pp-admin-message--${message.type}`}>
          {message.text}
        </div>
      )}

      {isFetching ? (
        <div className="pp-admin-loading">Loading configuration...</div>
      ) : (
        <>
          {/* Global Settings */}
          <div className="pp-admin-card">
            <h3 className="pp-admin-card-title">Global Settings</h3>

            <div className="pp-admin-settings-grid">
              {/* Enable / Disable */}
              <div className="pp-admin-field">
                <label className="pp-admin-label">Status</label>
                <div className="pp-admin-toggle-row">
                  <button
                    type="button"
                    className={`pp-admin-toggle ${enabled ? 'pp-admin-toggle--on' : ''}`}
                    onClick={() => setEnabled(v => !v)}
                  >
                    <span className="pp-admin-toggle-knob" />
                  </button>
                  <span className="pp-admin-toggle-label">
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Delay (ms) */}
              <div className="pp-admin-field">
                <label className="pp-admin-label" htmlFor="pp-delay">
                  Display Delay (ms)
                  <span className="pp-admin-hint"> 0 – 10000</span>
                </label>
                <input
                  id="pp-delay"
                  type="number"
                  min="0"
                  max="10000"
                  step="100"
                  value={delay}
                  onChange={e => setDelay(e.target.value)}
                  className="pp-admin-input"
                />
              </div>

              {/* Show on Mobile */}
              <div className="pp-admin-field">
                <label className="pp-admin-label">Show on Mobile</label>
                <div className="pp-admin-toggle-row">
                  <button
                    type="button"
                    className={`pp-admin-toggle ${showOnMobile ? 'pp-admin-toggle--on' : ''}`}
                    onClick={() => setShowOnMobile(v => !v)}
                  >
                    <span className="pp-admin-toggle-knob" />
                  </button>
                  <span className="pp-admin-toggle-label">
                    {showOnMobile ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>

              {/* Show on Desktop */}
              <div className="pp-admin-field">
                <label className="pp-admin-label">Show on Desktop</label>
                <div className="pp-admin-toggle-row">
                  <button
                    type="button"
                    className={`pp-admin-toggle ${showOnDesktop ? 'pp-admin-toggle--on' : ''}`}
                    onClick={() => setShowOnDesktop(v => !v)}
                  >
                    <span className="pp-admin-toggle-knob" />
                  </button>
                  <span className="pp-admin-toggle-label">
                    {showOnDesktop ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Banner Upload Card */}
          <div className="pp-admin-card">
            <h3 className="pp-admin-card-title">Popup Image Banner</h3>

            <div className="pp-admin-form-group">
              <div className="pp-admin-field">
                <label className="pp-admin-label">Banner Image File</label>
                <span className="pp-admin-hint" style={{ marginBottom: '8px' }}>
                  Choose a promotional poster to display. Image is required to show the popup.
                </span>
                
                <div className="pp-admin-image-section">
                  <div className="pp-admin-image-preview-wrapper">
                    {imagePreviewUrl ? (
                      <img src={imagePreviewUrl} alt="Preview" className="pp-admin-image-preview" />
                    ) : (
                      <span className="pp-admin-image-placeholder">No banner image uploaded</span>
                    )}
                  </div>
                  
                  <div className="pp-admin-image-actions">
                    <input
                      type="file"
                      id="pp-image-upload"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="pp-admin-file-input"
                    />
                    <label htmlFor="pp-image-upload" className="pp-admin-upload-btn">
                      Choose Image File
                    </label>
                    {imagePreviewUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="pp-admin-remove-image-btn"
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button Settings */}
          <div className="pp-admin-card">
            <h3 className="pp-admin-card-title">Call to Action (CTA) Button</h3>

            <div className="pp-admin-form-group">
              <div className="pp-admin-field">
                <label className="pp-admin-label">Button State</label>
                <div className="pp-admin-toggle-row">
                  <button
                    type="button"
                    className={`pp-admin-toggle ${buttonEnabled ? 'pp-admin-toggle--on' : ''}`}
                    onClick={() => setButtonEnabled(v => !v)}
                  >
                    <span className="pp-admin-toggle-knob" />
                  </button>
                  <span className="pp-admin-toggle-label">
                    {buttonEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {buttonEnabled && (
              <div className="pp-admin-settings-grid" style={{ marginTop: '15px' }}>
                <div className="pp-admin-field">
                  <label className="pp-admin-label" htmlFor="pp-btn-text">Button Text</label>
                  <input
                    id="pp-btn-text"
                    type="text"
                    placeholder="e.g. Shop Now"
                    value={buttonText}
                    onChange={e => setButtonText(e.target.value)}
                    className="pp-admin-input"
                  />
                </div>

                <div className="pp-admin-field">
                  <label className="pp-admin-label" htmlFor="pp-btn-link">
                    Button Link
                    <span className="pp-admin-hint"> e.g. /shop or https://...</span>
                  </label>
                  <input
                    id="pp-btn-link"
                    type="text"
                    placeholder="e.g. /shop"
                    value={buttonLink}
                    onChange={e => setButtonLink(e.target.value)}
                    className="pp-admin-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '28px' }}>
            <button
              type="button"
              className="btn"
              onClick={handleSave}
              disabled={isSaving}
              style={{ minWidth: '180px', fontSize: '1rem', padding: '12px 28px' }}
            >
              {isSaving ? 'Saving Changes...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPromotionalPopup;
